import React, { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import Layout from '../components/common/layout/Layout';
import LoginPage from '../../pages/login/LoginPage';
import SignupPage from '../../pages/signup/SignupPage';
import ForgotPage from '../../pages/forgot/ForgotPage';
import IntegrationsPage from '../../pages/integrations/IntegrationsPage';
import LookerStudioCallback from '../../pages/integrations/LookerStudioCallback';
import PricingPage from '../../pages/pricing/PricingPage';
import SettingsPage from '../../pages/settings/SettingsPage';
import TrialEndedPage from '../../pages/trial-ended/TrialEndedPage';
import PrivateRoutes from './PrivateRoutes';
import AddPromptPage from '../../pages/prompt/add/AddPromptPage';
import PreviewPromptPage from '../../pages/prompt/preview/PreviewPromptPage';
import TeamPage from '../../pages/settings/team/TeamPage';
import PeriodicReportsPage from '../../pages/periodic-reports/PeriodicReportsPage';
import ChangePasswordPage from '../../pages/settings/change-password/ChangePasswordPage';
import SubscriptionPage from '../../pages/settings/subscription/SubscriptionPage';
import MyAccountPage from '../../pages/settings/my-account/MyAccountPage';
import { StatsContextProvider } from '../contexts/StatsContexts';
import OrdersPage from '../../pages/settings/orders/OrdersPage';
import TeamEditPage from '../../pages/settings/team/edit/TeamEditPage';
import TeamAddPage from '../../pages/settings/team/add/TeamAddPage';
import BillingDataPage from '../../pages/settings/billing-data/BillingDataPage';
import ResetPasswordPage from '../../pages/reset-password/ResetPasswordPage';
import MagicLoginPage from '../../pages/magic-login/MagicLoginPage';
import AutoReactivationPage from '../../pages/auto-reactivation/AutoReactivationPage';
import VerifyEmailPage from '../../pages/verify-email/VerifyEmailPage';
import {
  ACCOUNTS,
  ADD,
  ADMIN,
  ALL,
  API_KEYS,
  BILLING_DATA,
  CHANGE_PASSWORD,
  CONTEXT_ANALYSIS,
  BOTS_ACTIVITY,
  BOT_ACCESS,
  DASHBOARD,
  EARLY_ACCESS,
  FAQ,
  FORGOT,
  GEO_CHECKLIST,
  AGENCY_STATS,
  GEO_REPORT,
  INSIGHTS,
  INTEGRATIONS,
  KEYWORDS,
  PROMPTS,
  LOGIN,
  MAGIC_LOGIN,
  MY_ACCOUNT,
  ORDERS,
  PERIODIC_REPORTS,
  PRICING,
  PROJECTS,
  PROJECTS_ADD,
  QUEST_FAQ,
  QUESTIONS,
  QUESTIONS_ID,
  REPORTS,
  SET_PASSWORD,
  SETTINGS,
  SIGNUP,
  SOURCES,
  SOURCES_DOMAIN,
  SOURCES_SOURCE,
  SUBSCRIPTION,
  SUGGESTED_PROMPTS,
  TAGS,
  TEAM,
  TEAM_ADD,
  TEAM_PREVIEW,
  TRIAL_ENDED,
  YOUR_ACCOUNTS,
  SOURCE_VERIFICATION,
  VERIFY_EMAIL,
  AUTO_REACTIVATION,
} from './path';
import ProjectsPage from '../../pages/projects/ProjectsPage';
import ProjectWizardPage from '../../pages/projects/wizard/ProjectWizardPage';
import { ProjectWizardProvider } from '../../pages/projects/wizard/contexts/ProjectWizardContext';
import Wrapper from '../components/common/wrapper/Wrapper';
import ProjectsPreviewPageSwitch from '../../pages/projects/preview/ProjectsPreviewPageSwitch';
import AdminPage from '../../pages/admin/AdminPage';
import DashboardPage from '../../pages/admin/dashboard/DashboardPage';
import FaqPage from '../../pages/faq/FaqPage';
import SimplePage from '../components/common/simple-page/SimplePage';
import AdminSettingsPage from '../../pages/admin/settings/AdminSettingsPage';
import AdminUsersPage from '../../pages/admin/users/AdminUsersPage';
import AdminUsersEditPage from '../../pages/admin/users/edit/AdminUsersEditPage';
import FrontPage from '../../pages/early-access/FrontPage';
import SuggestedPromptsPage from '../../pages/projects/suggested-prompts/SuggestedPromptsPage';
import AdminReportsPage from '../../pages/admin/reports/AdminReportsPage';
import YourAccountsPage from '../../pages/settings/your-accounts/YourAccountsPage';
import ApiKeysPage from '../../pages/settings/api-keys/ApiKeysPage';
import SourcesPage from '../../pages/sources/SourcesPage';
import DomainDetailPage from '../../pages/sources/domain-detail/DomainDetailPage';
import SourceDetailPage from '../../pages/sources/source-detail/SourceDetailPage';
import TagsPage from '../../pages/projects/tags/TagsPage';
import KeywordsPage from '../../pages/keywords/KeywordsPage';
import PromptsPage from '../../pages/prompts/PromptsPage';
import { FiltersContextProvider } from '../contexts/filters/FiltersContext';
import InsightsPage from '../../pages/insights/InsightsPage';
import ContextAnalysisPage from '../../pages/context-analysis/ContextAnalysisPage';
import GeoChecklistPage from '../../pages/geo-checklist/GeoChecklistPage';
import { CHECKLIST_TABS } from '../../pages/geo-checklist/data/checklistData';
import GeoReportPage from '../../pages/geo-report/GeoReportPage';
import BotAccessPage from '../../pages/bot-access/BotAccessPage';
import BotsActivityPage from '../../pages/bots-activity/BotsActivityPage';
import BotsActivityConnectPage from '../../pages/bots-activity/BotsActivityConnectPage';
import AgencyStatsPage from '../../pages/agency-stats/AgencyStatsPage';
import SourceVerificationPage from '../../pages/admin/source-verification/SourceVerificationPage';
import { AdminUserProvider } from '../contexts/admin/user/AdminUserContext';
import { useAccount } from '../contexts/account/AccountContexts';
import { useAuth } from '../contexts/auth/AuthContext';
import { getProjectsFromAccount } from '../helpers/project/ProjectHelper';
import useAccountData from '../hooks/account/UseAccountData';
import { ensureAttributionCookie } from '../services/account-attribution/AccountAttributionService';

const AppRoutes: React.FC = () => {
  const location = useLocation();
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (!isAuthenticated) {
      ensureAttributionCookie();
    }
  }, [isAuthenticated, location.pathname, location.search, location.hash]);

  interface RedirectProps {
    restUrl: string;
  }

  const RedirectToProjectStats: React.FC<RedirectProps> = ({ restUrl }) => {
    const { projectId } = useParams();
    const hash = location.hash || '';
    return <Navigate to={`${PROJECTS}/${projectId}/${restUrl}${ALL}${hash}`} replace />;
  };

  const RedirectToProjectMonitorStats: React.FC<RedirectProps> = ({ restUrl }) => {
    const { projectId, id } = useParams();
    const hash = location.hash || '';
    return (
      <Navigate to={`${PROJECTS}/${projectId}/${QUESTIONS}/${id}/${restUrl}${ALL}${hash}`} replace />
    );
  };

  const RedirectToSourcesStats: React.FC<RedirectProps> = ({ restUrl }) => {
    const { projectId } = useParams();
    const hash = location.hash || '';
    return <Navigate to={`${SOURCES}/${projectId}/${restUrl}${ALL}${hash}`} replace />;
  };

  const RedirectToInsights: React.FC<RedirectProps> = ({ restUrl }) => {
    const { projectId } = useParams();
    const hash = location.hash || '';
    return <Navigate to={`${INSIGHTS}/${projectId}/${restUrl}${ALL}${hash}`} replace />;
  };

  const RedirectToContextAnalysis: React.FC<RedirectProps> = ({ restUrl }) => {
    const { projectId } = useParams();
    const hash = location.hash || '';
    return <Navigate to={`${CONTEXT_ANALYSIS}/${projectId}/${restUrl}${ALL}${hash}`} replace />;
  };

  const RedirectToFirstProject: React.FC<{ baseRoute: string; restUrl?: string }> = ({
    baseRoute,
    restUrl,
  }) => {
    const { projectResponse, accountResponse } = useAccount();
    const loading = useAccountData();
    if (loading || projectResponse === null) {
      return null;
    }
    const projects = getProjectsFromAccount(accountResponse, projectResponse);
    const firstProject = projects[0];
    if (!firstProject) {
      return <Navigate to={PROJECTS} replace />;
    }
    if (restUrl) {
      return <Navigate to={`${baseRoute}/${firstProject.id}/${restUrl}${ALL}`} replace />;
    }
    return <Navigate to={`${baseRoute}/${firstProject.id}`} replace />;
  };

  const RedirectToGeoChecklist: React.FC = () => {
    const { projectId } = useParams();
    const defaultTab = CHECKLIST_TABS.find((tab) => !tab.hidden)?.slug ?? 'ai-visibility';
    return <Navigate to={`${GEO_CHECKLIST}/${projectId}/${defaultTab}`} replace />;
  };

  // Legacy alias: /bots-activity/:projectId/connect → /bots-activity/:projectId
  const BotsActivityConnectLegacyRedirect: React.FC = () => {
    const { projectId } = useParams();
    return <Navigate to={`${BOTS_ACTIVITY}/${projectId ?? ''}`} replace />;
  };

  const ExternalRedirect: React.FC<{ to: string }> = ({ to }) => {
    useEffect(() => {
      window.location.replace(to);
    }, [to]);
    return null;
  };

  return (
    <Routes>
      <Route path={LOGIN} element={<LoginPage />} />
      <Route path="/howto" element={<ExternalRedirect to="https://chatbeat.com/llm-seo" />} />
      <Route path={EARLY_ACCESS} element={<FrontPage />} />
      <Route path={SIGNUP} element={<SignupPage />} />
      <Route path={FORGOT} element={<ForgotPage />} />
      <Route path={SET_PASSWORD} element={<ResetPasswordPage />} />
      <Route path={MAGIC_LOGIN} element={<MagicLoginPage />} />
      <Route path={VERIFY_EMAIL} element={<SimplePage><VerifyEmailPage /></SimplePage>} />
      <Route path={AUTO_REACTIVATION} element={<AutoReactivationPage />} />

      {/* Looker Studio OAuth Callback */}
      <Route path="/looker-studio/callback" element={<LookerStudioCallback />} />

      <Route
        path={PROJECTS}
        element={
          <PrivateRoutes>
            <Wrapper>
              <Layout>
                <ProjectsPage />
              </Layout>
            </Wrapper>
          </PrivateRoutes>
        }
      />
      <Route
        path={`${PROJECTS}/:projectId${QUESTIONS}${ADD}`}
        element={
          <PrivateRoutes>
            <Wrapper>
              <Layout>
                <AddPromptPage />
              </Layout>
            </Wrapper>
          </PrivateRoutes>
        }
      />
      <Route
        path={`${PROJECTS}/:projectId/:period/:chat`}
        element={
          <PrivateRoutes>
            <Wrapper>
              <Layout>
                <FiltersContextProvider>
                  <ProjectsPreviewPageSwitch />
                </FiltersContextProvider>
              </Layout>
            </Wrapper>
          </PrivateRoutes>
        }
      />
      <Route
        path={`${PROJECTS}/:projectId${QUESTIONS_ID}/:period/:chat`}
        element={
          <PrivateRoutes>
            <Wrapper>
              <Layout>
                <FiltersContextProvider>
                  <PreviewPromptPage force={true} />
                </FiltersContextProvider>
              </Layout>
            </Wrapper>
          </PrivateRoutes>
        }
      />
      <Route
        path={PROJECTS_ADD}
        element={
          <PrivateRoutes>
            <Wrapper>
              <ProjectWizardProvider>
                <ProjectWizardPage />
              </ProjectWizardProvider>
            </Wrapper>
          </PrivateRoutes>
        }
      />
      <Route
        path={`${SUGGESTED_PROMPTS}/:projectId`}
        element={
          <PrivateRoutes>
            <Wrapper>
              <Layout>
                <StatsContextProvider>
                  <SuggestedPromptsPage />
                </StatsContextProvider>
              </Layout>
            </Wrapper>
          </PrivateRoutes>
        }
      />
      <Route
        path={`${TAGS}/:projectId`}
        element={
          <PrivateRoutes>
            <Wrapper>
              <Layout>
                <StatsContextProvider>
                  <TagsPage />
                </StatsContextProvider>
              </Layout>
            </Wrapper>
          </PrivateRoutes>
        }
      />
      <Route
        path={`${KEYWORDS}/:projectId`}
        element={
          <PrivateRoutes>
            <Wrapper>
              <Layout>
                <KeywordsPage />
              </Layout>
            </Wrapper>
          </PrivateRoutes>
        }
      />
      <Route
        path={`${PROMPTS}/:projectId`}
        element={
          <PrivateRoutes>
            <Wrapper>
              <Layout>
                <PromptsPage />
              </Layout>
            </Wrapper>
          </PrivateRoutes>
        }
      />
      <Route
        path={INTEGRATIONS}
        element={
          <PrivateRoutes>
            <Wrapper>
              <Layout dataPage="integrations">
                <IntegrationsPage />
              </Layout>
            </Wrapper>
          </PrivateRoutes>
        }
      />
      <Route
        path={FAQ}
        element={
          <PrivateRoutes>
            <Wrapper>
              <Layout dataPage="faq">
                <FaqPage />
              </Layout>
            </Wrapper>
          </PrivateRoutes>
        }
      />
      <Route
        path={QUEST_FAQ}
        element={
          <SimplePage>
            <FaqPage />
          </SimplePage>
        }
      />
      <Route
        path={`${ADMIN}${DASHBOARD}`}
        element={
          <PrivateRoutes>
            <Wrapper checkSuperAdmin={true}>
              <Layout>
                <AdminPage children={<DashboardPage />} restUrl="dashboard" />
              </Layout>
            </Wrapper>
          </PrivateRoutes>
        }
      />
      <Route
        path={`${ADMIN}${ACCOUNTS}`}
        element={
          <PrivateRoutes>
            <Wrapper checkSuperAdmin={true}>
              <Layout>
                <AdminUserProvider>
                  <AdminPage children={<AdminUsersPage />} restUrl="accounts" />
                </AdminUserProvider>
              </Layout>
            </Wrapper>
          </PrivateRoutes>
        }
      />
      <Route
        path={`${ADMIN}${ACCOUNTS}/:id`}
        element={
          <PrivateRoutes>
            <Wrapper checkSuperAdmin={true}>
              <Layout>
                <AdminUserProvider>
                  <AdminPage children={<AdminUsersEditPage />} restUrl="accounts" />
                </AdminUserProvider>
              </Layout>
            </Wrapper>
          </PrivateRoutes>
        }
      />
      <Route
        path={`${ADMIN}${REPORTS}`}
        element={
          <PrivateRoutes>
            <Wrapper checkSuperAdmin={true}>
              <Layout>
                <AdminPage children={<AdminReportsPage />} restUrl="reports" />
              </Layout>
            </Wrapper>
          </PrivateRoutes>
        }
      />
      <Route
        path={`${ADMIN}${SETTINGS}`}
        element={
          <PrivateRoutes>
            <Wrapper checkSuperAdmin={true}>
              <Layout>
                <AdminPage children={<AdminSettingsPage />} restUrl="settings" />
              </Layout>
            </Wrapper>
          </PrivateRoutes>
        }
      />
      <Route
        path={`${ADMIN}${SOURCE_VERIFICATION}`}
        element={
          <PrivateRoutes>
            <Wrapper checkSuperAdmin={true}>
              <Layout>
                <AdminPage children={<SourceVerificationPage />} restUrl="source-verification" />
              </Layout>
            </Wrapper>
          </PrivateRoutes>
        }
      />
      <Route
        path={PRICING}
        element={
          <PrivateRoutes>
            <Wrapper canShowPricing={true}>
              <Layout>
                <PricingPage />
              </Layout>
            </Wrapper>
          </PrivateRoutes>
        }
      />
      <Route
        path={TRIAL_ENDED}
        element={
          <PrivateRoutes>
            <Wrapper canShowPricing={true}>
              <Layout dataPage="trial-ended">
                <TrialEndedPage />
              </Layout>
            </Wrapper>
          </PrivateRoutes>
        }
      />
      <Route
        path={MY_ACCOUNT}
        element={
          <PrivateRoutes>
            <Wrapper>
              <Layout>
                <SettingsPage children={<MyAccountPage />} restUrl="my-account" />
              </Layout>
            </Wrapper>
          </PrivateRoutes>
        }
      />
      <Route
        path={CHANGE_PASSWORD}
        element={
          <PrivateRoutes>
            <Wrapper>
              <Layout>
                <SettingsPage children={<ChangePasswordPage />} restUrl="change-password" />
              </Layout>
            </Wrapper>
          </PrivateRoutes>
        }
      />
      <Route
        path={`${SOURCES}/:projectId/:period/:chat`}
        element={
          <PrivateRoutes>
            <Wrapper>
              <Layout>
                <FiltersContextProvider>
                  <SourcesPage />
                </FiltersContextProvider>
              </Layout>
            </Wrapper>
          </PrivateRoutes>
        }
      />
      <Route
        path={SOURCES_DOMAIN}
        element={
          <PrivateRoutes>
            <Wrapper>
              <Layout>
                <FiltersContextProvider>
                  <DomainDetailPage />
                </FiltersContextProvider>
              </Layout>
            </Wrapper>
          </PrivateRoutes>
        }
      />
      <Route
        path={SOURCES_SOURCE}
        element={
          <PrivateRoutes>
            <Wrapper>
              <Layout>
                <FiltersContextProvider>
                  <SourceDetailPage />
                </FiltersContextProvider>
              </Layout>
            </Wrapper>
          </PrivateRoutes>
        }
      />
      <Route
        path={`${INSIGHTS}/:projectId/:period/:chat`}
        element={
          <PrivateRoutes>
            <Wrapper>
              <Layout>
                <FiltersContextProvider>
                  <InsightsPage />
                </FiltersContextProvider>
              </Layout>
            </Wrapper>
          </PrivateRoutes>
        }
      />
      <Route
        path={`${CONTEXT_ANALYSIS}/:projectId/:period/:chat`}
        element={
          <PrivateRoutes>
            <Wrapper>
              <Layout>
                <FiltersContextProvider>
                  <ContextAnalysisPage />
                </FiltersContextProvider>
              </Layout>
            </Wrapper>
          </PrivateRoutes>
        }
      />
      <Route
        path={BILLING_DATA}
        element={
          <PrivateRoutes>
            <Wrapper canShowPricing={true}>
              <Layout>
                <SettingsPage children={<BillingDataPage />} restUrl="billing-data" />
              </Layout>
            </Wrapper>
          </PrivateRoutes>
        }
      />
      <Route
        path={SUBSCRIPTION}
        element={
          <PrivateRoutes>
            <Wrapper>
              <Layout>
                <SettingsPage children={<SubscriptionPage />} restUrl="subscription" />
              </Layout>
            </Wrapper>
          </PrivateRoutes>
        }
      />
      <Route
        path={ORDERS}
        element={
          <PrivateRoutes>
            <Wrapper canShowPricing={true}>
              <Layout>
                <SettingsPage children={<OrdersPage />} restUrl="orders" />
              </Layout>
            </Wrapper>
          </PrivateRoutes>
        }
      />
      <Route
        path={TEAM}
        element={
          <PrivateRoutes>
            <Wrapper>
              <Layout>
                <SettingsPage children={<TeamPage />} restUrl="team" />
              </Layout>
            </Wrapper>
          </PrivateRoutes>
        }
      />
      <Route
        path={TEAM_ADD}
        element={
          <PrivateRoutes>
            <Wrapper>
              <Layout>
                <SettingsPage children={<TeamAddPage />} restUrl="team" />
              </Layout>
            </Wrapper>
          </PrivateRoutes>
        }
      />
      <Route
        path={TEAM_PREVIEW}
        element={
          <PrivateRoutes>
            <Wrapper>
              <Layout>
                <SettingsPage children={<TeamEditPage />} restUrl="team" />
              </Layout>
            </Wrapper>
          </PrivateRoutes>
        }
      />
      <Route
        path={PERIODIC_REPORTS}
        element={
          <PrivateRoutes>
            <Wrapper>
              <Layout>
                <PeriodicReportsPage />
              </Layout>
            </Wrapper>
          </PrivateRoutes>
        }
      />
      <Route
        path={YOUR_ACCOUNTS}
        element={
          <PrivateRoutes>
            <Wrapper>
              <Layout>
                <SettingsPage children={<YourAccountsPage />} restUrl="your-accounts" />
              </Layout>
            </Wrapper>
          </PrivateRoutes>
        }
      />
      <Route
        path={API_KEYS}
        element={
          <PrivateRoutes>
            <Wrapper>
              <Layout>
                <SettingsPage children={<ApiKeysPage />} restUrl="api-keys" />
              </Layout>
            </Wrapper>
          </PrivateRoutes>
        }
      />
      <Route path={`${PROJECTS}/:projectId`} element={<RedirectToProjectStats restUrl="daily" />} />
      <Route
        path={`${PROJECTS}/:projectId/daily`}
        element={<RedirectToProjectStats restUrl="daily" />}
      />
      <Route
        path={`${PROJECTS}/:projectId/weekly`}
        element={<RedirectToProjectStats restUrl="weekly" />}
      />
      <Route
        path={`${PROJECTS}/:projectId/monthly`}
        element={<RedirectToProjectStats restUrl="monthly" />}
      />
      <Route
        path={`${PROJECTS}/:projectId${QUESTIONS_ID}`}
        element={<RedirectToProjectMonitorStats restUrl="daily" />}
      />
      <Route
        path={`${PROJECTS}/:projectId${QUESTIONS_ID}/daily`}
        element={<RedirectToProjectMonitorStats restUrl="daily" />}
      />
      <Route
        path={`${PROJECTS}/:projectId${QUESTIONS_ID}/weekly`}
        element={<RedirectToProjectMonitorStats restUrl="weekly" />}
      />
      <Route
        path={`${PROJECTS}/:projectId${QUESTIONS_ID}/monthly`}
        element={<RedirectToProjectMonitorStats restUrl="monthly" />}
      />
      <Route path={`${SOURCES}/:projectId`} element={<RedirectToSourcesStats restUrl="daily" />} />
      <Route path={`${SOURCES}/:projectId/daily`} element={<RedirectToSourcesStats restUrl="daily" />} />
      <Route
        path={`${SOURCES}/:projectId/weekly`}
        element={<RedirectToSourcesStats restUrl="weekly" />}
      />
      <Route
        path={`${SOURCES}/:projectId/monthly`}
        element={<RedirectToSourcesStats restUrl="monthly" />}
      />
      <Route path={`${INSIGHTS}/:projectId`} element={<RedirectToInsights restUrl="daily" />} />
      <Route path={`${INSIGHTS}/:projectId/daily`} element={<RedirectToInsights restUrl="daily" />} />
      <Route path={`${INSIGHTS}/:projectId/weekly`} element={<RedirectToInsights restUrl="weekly" />} />
      <Route
        path={`${INSIGHTS}/:projectId/monthly`}
        element={<RedirectToInsights restUrl="monthly" />}
      />
      <Route
        path={`${CONTEXT_ANALYSIS}/:projectId`}
        element={<RedirectToContextAnalysis restUrl="daily" />}
      />
      <Route
        path={`${CONTEXT_ANALYSIS}/:projectId/daily`}
        element={<RedirectToContextAnalysis restUrl="daily" />}
      />
      <Route
        path={`${CONTEXT_ANALYSIS}/:projectId/weekly`}
        element={<RedirectToContextAnalysis restUrl="weekly" />}
      />
      <Route
        path={`${CONTEXT_ANALYSIS}/:projectId/monthly`}
        element={<RedirectToContextAnalysis restUrl="monthly" />}
      />
      <Route
        path={CONTEXT_ANALYSIS}
        element={
          <PrivateRoutes>
            <RedirectToFirstProject baseRoute={CONTEXT_ANALYSIS} restUrl="daily" />
          </PrivateRoutes>
        }
      />
      <Route
        path={SOURCES}
        element={
          <PrivateRoutes>
            <RedirectToFirstProject baseRoute={SOURCES} restUrl="daily" />
          </PrivateRoutes>
        }
      />
      <Route
        path={TAGS}
        element={
          <PrivateRoutes>
            <RedirectToFirstProject baseRoute={TAGS} />
          </PrivateRoutes>
        }
      />
      <Route
        path={SUGGESTED_PROMPTS}
        element={
          <PrivateRoutes>
            <RedirectToFirstProject baseRoute={SUGGESTED_PROMPTS} />
          </PrivateRoutes>
        }
      />
      <Route
        path={PROMPTS}
        element={
          <PrivateRoutes>
            <RedirectToFirstProject baseRoute={PROMPTS} />
          </PrivateRoutes>
        }
      />
      <Route
        path={`${GEO_CHECKLIST}/:projectId/:tab`}
        element={
          <PrivateRoutes>
            <Wrapper>
              <Layout>
                <GeoChecklistPage />
              </Layout>
            </Wrapper>
          </PrivateRoutes>
        }
      />
      <Route path={`${GEO_CHECKLIST}/:projectId`} element={<RedirectToGeoChecklist />} />
      <Route
        path={GEO_CHECKLIST}
        element={
          <PrivateRoutes>
            <RedirectToFirstProject baseRoute={GEO_CHECKLIST} />
          </PrivateRoutes>
        }
      />
      <Route
        path={GEO_REPORT}
        element={
          <PrivateRoutes>
            <Wrapper>
              <Layout dataPage="geo-audit">
                <GeoReportPage />
              </Layout>
            </Wrapper>
          </PrivateRoutes>
        }
      />
      <Route
        path={AGENCY_STATS}
        element={
          <PrivateRoutes>
            <Wrapper>
              <Layout>
                <AgencyStatsPage />
              </Layout>
            </Wrapper>
          </PrivateRoutes>
        }
      />
      <Route
        path={`${BOT_ACCESS}/:projectId`}
        element={
          <PrivateRoutes>
            <Wrapper>
              <Layout>
                <BotAccessPage />
              </Layout>
            </Wrapper>
          </PrivateRoutes>
        }
      />
      <Route
        path={BOT_ACCESS}
        element={
          <PrivateRoutes>
            <RedirectToFirstProject baseRoute={BOT_ACCESS} />
          </PrivateRoutes>
        }
      />
      <Route
        path={`${BOTS_ACTIVITY}/:projectId/analysis`}
        element={
          <PrivateRoutes>
            <Wrapper>
              <Layout>
                <BotsActivityPage />
              </Layout>
            </Wrapper>
          </PrivateRoutes>
        }
      />
      <Route
        path={`${BOTS_ACTIVITY}/:projectId/connect`}
        element={
          <PrivateRoutes>
            <BotsActivityConnectLegacyRedirect />
          </PrivateRoutes>
        }
      />
      <Route
        path={`${BOTS_ACTIVITY}/:projectId`}
        element={
          <PrivateRoutes>
            <Wrapper>
              <Layout>
                <BotsActivityConnectPage />
              </Layout>
            </Wrapper>
          </PrivateRoutes>
        }
      />
      <Route
        path={BOTS_ACTIVITY}
        element={
          <PrivateRoutes>
            <RedirectToFirstProject baseRoute={BOTS_ACTIVITY} />
          </PrivateRoutes>
        }
      />
      <Route path={SETTINGS} element={<Navigate to="/settings/my-account" />} />
      <Route path={ADMIN} element={<Navigate to="/admin/accounts" />} />
      <Route path="/*" element={<Navigate to={PROJECTS} />} />
    </Routes>
  );
};

export default AppRoutes;
